"use client";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import React from "react";
import { ButtonStyleType, SpacingType, sectionVariants } from "@/types";
import Link from "next/link";
import Image from '@/components/ui/Image';
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";

interface Hero13Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  contents?: {
    badge?: string;
    title?: string;
    content?: string;
    microCopy?: string; 
    leftTopImage?: string;
    leftTopImageAltText?: string;
    leftBottomImage?: string;
    leftBottomImageAltText?: string;
    rightTopImage?: string;
    rightTopImageAltText?: string;
    rightBottomImage?: string;
    rightBottomImageAltText?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    secondaryButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
    secondaryButtonStyle?: ButtonStyleType;
  };
  attributes?: React.HTMLProps<HTMLElement>;
}

const Hero13 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  contents,
  attributes,
}: Hero13Props) => {

  return (
    <SectionWrapper
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="hero13"
      attributes={attributes}
      usedInPageNav={false}
      pageNavTitle="Hero 13"
    > 
      <div className="container"> 
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24">
          <div className="w-full lg:w-12/24 flex items-center">
            <div className="flex flex-col gap-6 text-center lg:text-left">
              {contents?.badge && (
                <Badge variant="outline" bold className="mx-auto lg:mx-0">
                  {contents?.badge}
                </Badge>
              )}
              {contents?.title && (
                <h1>
                  {parse(contents.title)}
                </h1>
              )}
              {contents?.content && (
                <div className="mt-4 paragraph-lg">
                  {parse(contents?.content)}
                </div>
              )}
              {contents?.hasButton && (
                <div className="mt-2">
                  <div className="flex flex-col items-center gap-4 lg:flex-row">
                    <Button
                      asChild
                      size='lg'
                      className="w-full sm:w-auto"
                      variant={contents?.buttonStyle || "primary"}
                    >
                      <Link href={contents?.buttonLink ?? ""}>{contents?.buttonText}</Link>
                    </Button>
                    {contents?.secondaryButton && (
                      <Button
                        className="w-full sm:w-auto"
                        variant={contents?.secondaryButtonStyle || "outline"}
                        asChild
                        size='lg'
                      >
                        <Link href={contents?.secondaryButtonLink ?? ""}> 
                          {contents?.secondaryButtonText}
                        </Link>
                      </Button>
                    )}
                  </div>
                  {contents?.microCopy && (
                    <p className="mt-2 paragraph-sm text-muted-foreground">
                      {contents?.microCopy}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="w-full lg:w-12/24 flex items-center justify-center">
            <div className="relative aspect-7/7 h-full w-full">
              {contents?.leftTopImage && (
                <div className="absolute top-[0%] right-[50%] flex aspect-square w-[26%] justify-center">
                  <Image src={contents?.leftTopImage} alt={contents?.leftTopImageAltText ?? ""} title={contents?.leftTopImageAltText ?? ""} fill className="object-cover rounded-2xl" priority fetchPriority="high" />
                </div>
              )}
              {contents?.leftBottomImage && (
                <div className="absolute top-[30%] right-[50%] flex aspect-5/6 w-[46%] justify-center">
                  <Image src={contents?.leftBottomImage} alt={contents?.leftBottomImageAltText ?? ""} title={contents?.leftBottomImageAltText ?? ""} fill className="object-cover rounded-2xl" priority fetchPriority="high" />
                </div>
              )}
              {contents?.rightTopImage && (
                <div className="absolute bottom-[30%] left-[54%] flex aspect-5/6 w-[46%] justify-center">
                  <Image src={contents?.rightTopImage} alt={contents?.rightTopImageAltText ?? ""} title={contents?.rightTopImageAltText ?? ""} fill className="object-cover rounded-2xl" priority fetchPriority="high" />
                </div>
              )}
              {contents?.rightBottomImage && (
                <div className="absolute bottom-[0%] left-[54%] flex aspect-square w-[26%] justify-center">
                  <Image src={contents?.rightBottomImage} alt={contents?.rightBottomImageAltText ?? ""} title={contents?.rightBottomImageAltText ?? ""} fill className="object-cover rounded-2xl" priority fetchPriority="high" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Hero13 };
